import { Component } from 'react'
import {
  folderUsageBar,
  formatPercent,
  formatSizeGb,
} from '@/lib/folderUsage'
import { cn } from '@/lib/utils'

export class FolderUsageErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  render() {
    if (this.state.error) {
      return (
        <p className="text-sm text-muted-foreground">
          Could not render the folder breakdown.
          {this.props.fallbackHint ? ` ${this.props.fallbackHint}` : ''}
        </p>
      )
    }
    return this.props.children
  }
}

export function FolderUsageList({
  folders,
  percentKey = 'percent_of_used',
  className,
  emptyMessage = 'No folder usage data.',
}) {
  const rows = Array.isArray(folders) ? folders : []
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>
  }

  const maxPercent = rows.reduce((max, row) => {
    const pct = Number(row?.[percentKey])
    if (!Number.isFinite(pct)) return max
    return pct > max ? pct : max
  }, 0)

  return (
    <div className={cn('space-y-2', className)}>
      {rows.map((row, index) => {
        const pctRaw = row?.[percentKey]
        const pct = Number.isFinite(Number(pctRaw)) ? Number(pctRaw) : null
        const barPct = maxPercent > 0 && pct != null
          ? (pct / maxPercent) * 100
          : pct
        const label = row?.name || row?.path || '—'
        const fileCount = Number.isFinite(Number(row?.file_count))
          ? Number(row.file_count)
          : null

        return (
          <div
            key={`${row?.path || label}-${row?.size_gb ?? 'x'}-${index}`}
            className="space-y-0.5"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0 text-sm">
              <span
                className="font-medium truncate max-w-[min(100%,28rem)]"
                title={row?.path || label}
              >
                {label}
              </span>
              <span className="tabular-nums text-muted-foreground shrink-0">
                {formatSizeGb(row?.size_gb)}
                {pct != null && (
                  <span className="ml-2 text-foreground">{formatPercent(pct)}</span>
                )}
                {fileCount != null && (
                  <span className="ml-2">({fileCount.toLocaleString('en-GB')} files)</span>
                )}
              </span>
            </div>
            <pre
              className="text-xs leading-none text-primary/80 overflow-x-auto"
              aria-hidden="true"
            >
              {folderUsageBar(barPct)}
            </pre>
            {row?.path && row.path !== label && (
              <p className="text-xs text-muted-foreground truncate" title={row.path}>
                {row.path}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}
